import React, { FC, useEffect, useState } from "react";
import { useOrganisationalUnitsQuery } from "../../../graphql/query";
import { VpdDataImportType } from "../../../graphql/__generated_types__/globalTypes";
import { Button, Cascader, Checkbox, Form, Modal, Space, Table, Tooltip } from "antd";
import { FilterFilled, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import produce from "immer";
import { Icons, orgUnitCascaderValue, orgUnitFromCascadeValue } from "@design-system";
import { isEqual } from "lodash";
import TextArea from "antd/lib/input/TextArea";
import { useForm } from "antd/lib/form/Form";
import { CreateLibraryAssumptionVariables } from "@graphql-types/CreateLibraryAssumption";
import { FindLibraryAssumptions_libraryAssumptions_organisationalUnits } from "@graphql-types/FindLibraryAssumptions";
import { compareAsc } from "date-fns";
import { UpdateLibraryAssumptionsVariables } from "@graphql-types/UpdateLibraryAssumptions";
import { VPDLabel } from "~/context/taxonomy-context";

type Update = {
  id: number;
  default?: boolean | null;
  mandatory?: boolean | null;
  changed: boolean;
  text: string;
  organisationalUnits?: { id: number }[] | null;
  toDelete: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: { id: number; userName: string; displayName: string } | null;
};

export type AssumptionsExclusionsProps = {
  type: VpdDataImportType;
  items: LibraryItem[];
  createLibraryItem: (data: {
    variables: CreateLibraryAssumptionVariables | CreateLibraryAssumptionVariables;
    onCompleted: () => void;
  }) => void;
  updateLibraryItem: (data: {
    variables: UpdateLibraryAssumptionsVariables | UpdateLibraryAssumptionsVariables;
    onCompleted: () => void;
  }) => void;
  updateLoading: boolean;
  queryLoading: boolean;
  handleOrgUnitFilterChange: (id: number | null) => void;
};

type LibraryItem = {
  id: number;
  text: string;
  organisationalUnits: FindLibraryAssumptions_libraryAssumptions_organisationalUnits[] | null;
  mandatory: boolean | null;
  default: boolean | null;
  createdAt: any;
  updatedAt: Date;
  createdBy?: { id: number; userName: string; displayName: string } | null;
};

export const AssumptionsExclusionsDataImport: FC<AssumptionsExclusionsProps> = ({
  type,
  items,
  createLibraryItem,
  updateLibraryItem,
  updateLoading,
  queryLoading,
  handleOrgUnitFilterChange,
}) => {
  const { data: practiceAreas, loading: paLoading } = useOrganisationalUnitsQuery();

  const PA_LABEL = VPDLabel("PracticeArea");

  const initialState = items?.reduce((acc, curr) => {
    if (!curr) return acc;
    acc[curr.id] = {
      id: curr.id,
      default: curr.default,
      mandatory: curr.mandatory,
      changed: false,
      text: curr.text,
      organisationalUnits: curr.organisationalUnits?.length
        ? curr.organisationalUnits
        : [{ id: 0 }],
      toDelete: false,
      createdAt: curr.createdAt,
      updatedAt: curr.updatedAt,
      createdBy: curr.createdBy,
    };
    return acc;
  }, {} as Record<number, Update>);

  const [formState, setFormState] = useState<Record<string, Update>>(initialState);
  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [selectedFilterText, setSelectedFilterText] = useState<string>("");

  const [modalForm] = useForm();

  const paOptions =
    practiceAreas?.organisationalUnits
      .filter((x) => !x.parent?.id)
      .map((pa) => {
        return {
          value: pa.id,
          label: pa.name,
          children: pa.children?.map((y) => ({ value: y.id, label: y.name })) || [],
        };
      }) ?? [];

  paOptions.unshift({
    value: 0,
    label: "Firm Wide",
    children: [],
  });

  useEffect(() => {
    setFormState(initialState);
  }, [items]);

  const handleModalOk = () => {
    modalForm
      .validateFields()
      .then((values) => {
        createLibraryItem({
          variables: {
            data: {
              text: values.text,
            },
          },
          onCompleted: () => setShowAddNewModal(false),
        });
      })
      .catch((err) => {
        console.log({ err });
      });
  };

  const onSubmit = () => {
    const changed = Object.values(formState).filter((x) => x.changed === true);

    if (changed.length === 0) return;

    updateLibraryItem({
      variables: {
        data: changed.map((x) => ({
          id: x.id,
          default: x.default,
          mandatory: x.mandatory,
          text: x.text,
          organisationalUnitIds: x.organisationalUnits?.filter((x) => x.id !== 0)?.map((x) => x.id),
          delete: x.toDelete,
          firmWide: x.organisationalUnits?.map((x) => x.id).includes(0),
        })),
      },
      onCompleted: () => {
        setFormState(initialState);
      },
    });
  };

  const onTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>, id: number) => {
    setFormState(
      produce(formState, (draft) => {
        draft[id].text = e.target.value;
        draft[id].changed = e.target.value !== initialState[id].text;
      })
    );
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    handleOrgUnitFilterChange(null);
  };

  const onOptionChange = (val: any[], id: number) => {
    setFormState(
      produce(formState, (draft) => {
        const values = val.map((x) => ({
          id: orgUnitFromCascadeValue(x),
        }));

        // If this is a parent.
        if (values.length === 1) {
          const parent = practiceAreas?.organisationalUnits.find((x) => x.id === values[0].id);

          if (parent) {
            values.push(...(parent.children?.map((x) => ({ id: x.id })) || []));
          }
        }

        draft[id].organisationalUnits = values;

        const originalOrgUnitIds = initialState[id].organisationalUnits?.map((x) => x.id);

        const match = isEqual(
          values.map((x) => x.id),
          originalOrgUnitIds
        );

        draft[id].changed = !match;
      })
    );
  };

  const loading = queryLoading || updateLoading || paLoading;

  return (
    <div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={loading || showAddNewModal}
            onClick={() => setShowAddNewModal(true)}
          >
            Add New
          </Button>
          <Button
            size="large"
            disabled={Object.values(formState).every((x) => x.changed === false)}
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => {
              onSubmit();
            }}
          >
            Save
          </Button>
        </div>
        <Table
          scroll={{ y: "calc(100vh - 350px)" }}
          columns={[
            {
              title: "Text",
              dataIndex: "text",
              key: "text",
              width: "30rem",
              render: (val, record) => {
                return (
                  <TextArea
                    value={val}
                    onChange={(val) => {
                      onTextChange(val, record.id);
                    }}
                  />
                );
              },
            },
            {
              title: () => (
                <span>
                  {PA_LABEL}
                  {selectedFilterText && (
                    <span style={{ fontSize: 12, marginLeft: 10 }}>{selectedFilterText}</span>
                  )}
                </span>
              ),
              key: "practiceArea",
              width: "30rem",
              filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
              filterDropdown({ clearFilters, selectedKeys, setSelectedKeys, confirm }) {
                return (
                  <div style={{ padding: 8 }}>
                    <Cascader
                      onChange={(val) => {
                        setSelectedKeys(val);
                        confirm();
                        if (!val) {
                          handleOrgUnitFilterChange(null);
                          confirm();
                          return;
                        }
                        const id = val[1] ? val[1] : val[0];
                        const selectedName = val[1]
                          ? practiceAreas?.organisationalUnits
                              .flatMap((x) => x.children)
                              .find((c) => c?.id === val[1])
                          : practiceAreas?.organisationalUnits.find((x) => x?.id === val[0]);

                        const filterText = selectedName
                          ? selectedName.name
                          : id === 0
                          ? "Firm Wide"
                          : "";

                        setSelectedFilterText(filterText);
                        handleOrgUnitFilterChange(Number(id));
                      }}
                      placeholder={`Select ${PA_LABEL}`}
                      options={paOptions}
                      showSearch={true}
                      value={selectedKeys as any}
                    />
                    <Space>
                      <Button
                        onClick={() => {
                          clearFilters && handleReset(clearFilters);
                          setSelectedFilterText("");
                          confirm();
                        }}
                        size="small"
                        style={{ width: 70, marginLeft: 3 }}
                      >
                        Reset
                      </Button>
                    </Space>
                  </div>
                );
              },
              render: (_, record) => {
                if (paOptions?.length ?? 0 > 0) {
                  return (
                    <Cascader
                      style={{ width: "100%" }}
                      key={record.id}
                      loading={paLoading}
                      onChange={(val) => {
                        return onOptionChange(val, record.id);
                      }}
                      multiple
                      maxTagCount="responsive"
                      value={
                        formState[record.id]?.organisationalUnits?.map((x) => x.id).includes(0)
                          ? [0]
                          : formState[record.id]?.organisationalUnits?.map((x) =>
                              orgUnitCascaderValue(practiceAreas?.organisationalUnits, x.id)
                            )
                      }
                      placeholder={`Select ${PA_LABEL}`}
                      options={paOptions.map((x) => {
                        const disabled =
                          x.value !== 0 &&
                          formState[record.id].organisationalUnits?.map((x) => x.id).includes(0);
                        return {
                          ...x,
                          label: disabled ? (
                            <Tooltip
                              placement="topLeft"
                              title={`A ${type} can be either Firm Wide or ${PA_LABEL} specific but not both.`}
                            >
                              {x.label}
                            </Tooltip>
                          ) : (
                            x.label
                          ),

                          disabled,
                          loading: updateLoading || paLoading,
                          children: x.children?.map((y) => ({ ...y, disabled })),
                        };
                      })}
                      showSearch={true}
                      allowClear={false}
                    />
                  );
                }
              },
            },
            {
              title: "Auto Select",
              dataIndex: "default",
              width: "10rem",
              key: "default",
              render: (_, row) => {
                return (
                  <Checkbox
                    disabled={formState[row.id]?.mandatory ?? false}
                    onChange={(e) => {
                      const currState = formState[row.id];
                      setFormState((curr) => ({
                        ...curr,
                        [row.id]: {
                          ...currState,
                          default: e.target.checked,
                          changed: e.target.checked !== initialState[row.id].default,
                        },
                      }));
                    }}
                    checked={formState[row.id]?.default ?? false}
                  />
                );
              },
            },
            {
              title: "Mandatory",
              dataIndex: "mandatory",
              width: "10rem",
              key: "mandatory",
              render: (_, row) => {
                return (
                  <Checkbox
                    onChange={(e) => {
                      const key = row.id;
                      const currState = formState[key];
                      setFormState((curr) => ({
                        ...curr,
                        [row.id]: {
                          ...currState,
                          mandatory: e.target.checked,
                          default: e.target.checked,
                          changed: e.target.checked !== initialState[key]?.mandatory,
                        },
                      }));
                    }}
                    checked={formState[row.id]?.mandatory ?? false}
                  />
                );
              },
            },
            {
              title: "Last updated",
              dataIndex: "updatedAt",
              key: "updatedAt",
              render: (_, row) => {
                return new Date(row.updatedAt).toLocaleString();
              },
              sorter: (a, b) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
              width: "15rem",
            },
            {
              title: "Created by",
              dataIndex: ["createdBy", "displayName"],
              key: "createdBy",
              width: "15rem",
            },
            {
              title: Icons.Delete,
              dataIndex: "delete",
              key: "delete",
              render: (_, row) => {
                return (
                  <Checkbox
                    checked={formState[row.id]?.toDelete ?? false}
                    onChange={(e) => {
                      const key = row.id;
                      const currState = formState[key];
                      setFormState((curr) => ({
                        ...curr,
                        [row.id]: {
                          ...currState,
                          toDelete: e.target.checked,
                          changed: e.target.checked,
                        },
                      }));
                    }}
                  />
                );
              },
            },
          ]}
          loading={loading}
          pagination={{
            onChange() {
              if (Object.values(formState).every((x) => x.changed === false)) return;
              onSubmit();
            },
            showSizeChanger: true,
            pageSizeOptions: [10],
          }}
          dataSource={
            formState
              ? Object.values(formState).sort((a, b) =>
                  compareAsc(new Date(b.createdAt), new Date(a.createdAt))
                )
              : []
          }
          rowKey="  id"
        />
      </div>

      <Modal
        title="Create New"
        centered
        width={700}
        onOk={handleModalOk}
        onCancel={() => setShowAddNewModal(false)}
        open={showAddNewModal}
        destroyOnClose
        closable
      >
        <Form preserve={false} form={modalForm}>
          <Form.Item rules={[{ required: true }]} name={"text"} label="Text">
            <TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
